'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { useRouter } from 'next/navigation';
import { openPlansDialog } from '@/components/plans-dialog-host';
import {
  ChevronRight,
  LayoutGrid,
  Plus,
  Loader2,
  Pencil,
  Trash2,
  Copy,
  ExternalLink,
  CornerDownRight,
  Share2,
  SquareTerminal,
  Braces,
  Cpu,
  FolderTree,
  FolderOpen,
  MoreHorizontal,
  Settings,
} from 'lucide-react';
import { openWorkspaceSettingsDialog } from '@/components/workspace/workspace-settings-dialog';
import { useWorkspace, useWorkspaceActions } from '@/components/workspace/store';
import { PanelSearchInput } from '@/components/workspace/panels/panel-ui';
import { Avatar } from '@/components/avatar';
import {
  FileContextMenu,
  useFileContextMenu,
  type ContextMenuItem,
} from '@/components/workspace/panels/file-context-menu';
import { kindVisual, WidgetMark } from '@/components/workspace/panels/content-preview';
import { resolveContentKind, itemKindLabel, parseWidgetIcon } from '@/lib/content/app-identity';
import { ShareDialog } from '@/components/sharing/share-dialog';
import {
  emitWorkspaceListChanged,
  onWorkspaceListChanged,
  emitWorkspaceItemsChanged,
  onWorkspaceItemsChanged,
} from '@/lib/workspaces/list-events';
import {
  openWorkspacePanel,
  requestContentBrowse,
} from '@/components/workspace/panels/panel-events';
import { PopoverPortal } from '@/components/ui/popover-portal';
import { cn } from '@/lib/utils';

/**
 * Workspaces tab — SPACES SHOW WHAT IS OPEN. Each workspace row summarizes
 * its open apps and people; expanding lists the apps themselves (from the
 * server-side canvas snapshot, so it works across workspaces without a tab),
 * plus a Browse files hand-off to the Content panel. The old per-workspace
 * FILE TREE is gone: files live in ONE place (Content), and the one thing
 * this panel used to hide — the open app windows — is now the thing it
 * shows. Also the single home for workspace management (create / rename /
 * share / duplicate / delete / settings).
 */

// ---------------------------------------------------------------------------
// Types (mirror lib/workspaces/queries.ts WorkspaceSummary)
// ---------------------------------------------------------------------------

type OpenApp = {
  itemId: string;
  type: string;
  refId: string | null;
  name: string;
  contentType: string | null;
  mimeType: string | null;
  projectMime: string | null;
  appIcon?: string | null;
  widgetType?: string | null;
  thumbnailPath: string | null;
  username: string | null;
  avatarUrl: string | null;
  minimized: boolean;
};

type WsRoot = {
  id: string;
  name: string;
  visibility: string;
  itemCount: number;
  appCount: number;
  apps: OpenApp[];
  memberCount: number;
  owned?: boolean;
  updatedAt: string;
};

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(String(res.status));
  return (await res.json()) as T;
}

const GENERIC_ICON: Record<string, typeof Cpu> = {
  terminal: SquareTerminal,
  ssh: SquareTerminal,
  python: Braces,
  daemon: Cpu,
  files: FolderTree,
};

function AppIcon({ app }: { app: OpenApp }) {
  if (app.type === 'agent') {
    return (
      <Avatar
        seed={app.username || app.name}
        type="agent"
        src={app.avatarUrl ?? null}
        className="h-5 w-5"
      />
    );
  }
  if (app.type === 'content') {
    // A published app wears its manifest-declared mark; a document wears its
    // format glyph from the registry (the same mark family as its app).
    const iconRef = parseWidgetIcon(app.appIcon);
    if (iconRef) {
      return (
        <WidgetMark
          iconRef={iconRef}
          seed={app.widgetType ?? app.refId ?? app.itemId}
          name={app.name}
          className="h-5 w-5 rounded-md"
          iconClassName="h-3.5 w-3.5"
        />
      );
    }
    const resolved = resolveContentKind({
      contentType: app.contentType ?? 'folder',
      mimeType: app.mimeType ?? '',
      metadata: app.projectMime ? { projectMime: app.projectMime } : null,
      displayName: app.name,
    });
    const { Icon, tone } = kindVisual(resolved);
    return (
      <span className={cn('inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md', tone)}>
        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
      </span>
    );
  }
  const I = GENERIC_ICON[app.type] ?? Cpu;
  return (
    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-foreground/8 text-muted-foreground">
      <I className="h-3.5 w-3.5" />
    </span>
  );
}

function appKindLine(app: OpenApp): string {
  const base =
    app.type === 'agent'
      ? 'Agent'
      : app.type === 'content'
        ? itemKindLabel({
            contentType: app.contentType,
            mimeType: app.mimeType,
            projectMime: app.projectMime,
          })
        : 'App';
  return app.minimized ? `${base} · minimized` : base;
}

function wsSummaryLine(ws: WsRoot): string {
  const apps =
    ws.appCount === 0
      ? 'Nothing open'
      : `${ws.appCount} app${ws.appCount === 1 ? '' : 's'} open`;
  return ws.memberCount > 1 ? `${apps} · ${ws.memberCount} people` : apps;
}

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

export function WorkspacesTab({
  scopeToCurrent = false,
  hideCreate = false,
}: { scopeToCurrent?: boolean; hideCreate?: boolean } = {}) {
  const t = useT();
  const router = useRouter();
  const workspace = useWorkspace();
  const { renameWorkspace } = useWorkspaceActions();

  const [roots, setRoots] = useState<WsRoot[]>([]);
  const [loadingRoots, setLoadingRoots] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set([workspace.id]), // current workspace auto-expanded
  );

  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [confirmDeleteWs, setConfirmDeleteWs] = useState<WsRoot | null>(null);
  const [shareWorkspaceId, setShareWorkspaceId] = useState<string | null>(null);
  // Inline name dialog (replaces window.prompt for new workspace).
  const [namePrompt, setNamePrompt] = useState<{
    title: string;
    placeholder: string;
    submitLabel: string;
    onSubmit: (name: string) => void;
  } | null>(null);

  type MenuTarget = { kind: 'workspace'; ws: WsRoot } | { kind: 'app'; wsId: string; app: OpenApp };
  const ctx = useFileContextMenu<MenuTarget>();

  // --- roots ---------------------------------------------------------------
  const loadRoots = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const data = await fetchJson<{ workspaces: WsRoot[] }>('/api/users/me/workspaces');
        if (signal?.aborted) return;
        const all = (data.workspaces ?? []).map((w) => ({
          ...w,
          apps: Array.isArray(w.apps) ? w.apps : [],
          appCount: typeof w.appCount === 'number' ? w.appCount : (w.apps?.length ?? 0),
          memberCount: typeof w.memberCount === 'number' ? w.memberCount : 1,
        }));
        setRoots(scopeToCurrent ? all.filter((w) => w.id === workspace.id) : all);
      } catch {
        if (!signal?.aborted) setRoots([]);
      } finally {
        if (!signal?.aborted) setLoadingRoots(false);
      }
    },
    [scopeToCurrent, workspace.id],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    void loadRoots(ctrl.signal);
    return () => ctrl.abort();
  }, [loadRoots]);

  // Live refresh: workspace-list and canvas-item changes both alter what
  // this panel shows (names, open apps), so either reloads the summaries.
  useEffect(() => onWorkspaceListChanged(() => void loadRoots()), [loadRoots]);
  useEffect(() => onWorkspaceItemsChanged(() => void loadRoots()), [loadRoots]);

  // --- open behaviors ------------------------------------------------------
  const openWorkspace = useCallback(
    (ws: WsRoot) => {
      if (ws.id !== workspace.id) router.push(`/workspace/${ws.id}`);
      else {
        setExpanded((s) => {
          const n = new Set(s);
          if (n.has(ws.id)) n.delete(ws.id);
          else n.add(ws.id);
          return n;
        });
      }
    },
    [workspace.id, router],
  );

  const openApp = useCallback(
    (wsId: string, app: OpenApp) => {
      if (wsId !== workspace.id) {
        // Another workspace: switch to it (the canonical navigation); the
        // mount restore lands on its saved screen.
        router.push(`/workspace/${wsId}`);
        return;
      }
      // Current workspace: fly to that app's screen via the canvas chokepoint
      // (restores a minimized window, brings it to front, frames the screen).
      window.dispatchEvent(
        new CustomEvent('ws:screen', { detail: { kind: 'item', itemId: app.itemId } }),
      );
    },
    [workspace.id, router],
  );

  const browseFiles = useCallback((ws: WsRoot) => {
    requestContentBrowse({ id: ws.id, name: ws.name || 'Untitled', shared: ws.owned === false });
    openWorkspacePanel('left', { tab: 'content' });
  }, []);

  const removeApp = useCallback(async (wsId: string, app: OpenApp) => {
    try {
      await fetch(`/api/workspaces/${wsId}/items/${app.itemId}`, { method: 'DELETE' });
      emitWorkspaceItemsChanged();
    } catch {
      /* refresh reconciles */
    }
  }, []);

  // --- workspace management ------------------------------------------------
  const beginRename = useCallback((ws: WsRoot) => {
    setRenamingId(ws.id);
    setRenameDraft(ws.name);
  }, []);

  const commitRename = useCallback(
    async (ws: WsRoot) => {
      const next = renameDraft.trim();
      setRenamingId(null);
      if (!next || next === ws.name) return;
      try {
        if (ws.id === workspace.id) {
          // The workspace we're standing in: go through the store so every
          // `useWorkspace()` reader picks the new name up immediately.
          await renameWorkspace(next);
        } else {
          await fetch(`/api/workspaces/${ws.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: next }),
          });
        }
        emitWorkspaceListChanged();
        router.refresh();
      } catch {
        /* refresh will reconcile */
      }
    },
    [renameDraft, workspace.id, renameWorkspace, router],
  );

  const createWorkspace = useCallback(
    async (name: string) => {
      try {
        const res = await fetch('/api/workspaces', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: name.trim() || 'Untitled workspace' }),
        });
        if (res.ok) {
          const data = await res.json();
          emitWorkspaceListChanged();
          if (data?.workspace?.id) router.push(`/workspace/${data.workspace.id}`);
        } else if (res.status === 403) {
          // Per-plan workspace cap reached → open the Plans dialog.
          const j = await res.clone().json().catch(() => null);
          if (j?.code === 'upgrade_required') openPlansDialog();
        }
      } catch {
        /* ignore */
      }
    },
    [router],
  );

  const newWorkspace = useCallback(() => {
    setNamePrompt({
      title: t('workspace.canvas.newWorkspace'),
      placeholder: 'Workspace name',
      submitLabel: 'Create',
      onSubmit: (name) => void createWorkspace(name),
    });
  }, [createWorkspace]);

  const duplicateWorkspace = useCallback(
    async (ws: WsRoot) => {
      try {
        const res = await fetch(`/api/workspaces/clone-from/${ws.id}`, { method: 'POST' });
        if (res.ok) {
          emitWorkspaceListChanged();
          const data = await res.json().catch(() => null);
          if (data?.workspace?.id) router.push(`/workspace/${data.workspace.id}`);
        }
      } catch {
        /* ignore */
      }
    },
    [router],
  );

  const deleteWorkspace = useCallback(
    async (ws: WsRoot) => {
      setConfirmDeleteWs(null);
      try {
        const res = await fetch(`/api/workspaces/${ws.id}`, { method: 'DELETE' });
        if (res.ok) {
          emitWorkspaceListChanged();
          if (ws.id === workspace.id) router.push('/workspace');
        }
      } catch {
        /* ignore */
      }
    },
    [router, workspace.id],
  );

  // --- context menus -------------------------------------------------------
  const menuItems = useMemo<ContextMenuItem[]>(() => {
    const target = ctx.target;
    if (!target) return [];
    const icon = (I: typeof Pencil) => <I className="h-4 w-4" />;

    if (target.kind === 'workspace') {
      const ws = target.ws;
      const isCurrent = ws.id === workspace.id;
      return [
        {
          id: 'open',
          label: isCurrent ? t('workspace.panels.expand') : t('workspace.panels.openWorkspace'),
          icon: icon(isCurrent ? ChevronRight : ExternalLink),
          onSelect: () => openWorkspace(ws),
        },
        {
          id: 'browse',
          label: t('workspace.panels.browseFiles'),
          icon: icon(FolderOpen),
          onSelect: () => browseFiles(ws),
        },
        { id: 'share', label: 'Share…', icon: icon(Share2), onSelect: () => setShareWorkspaceId(ws.id) },
        // Settings target the live workspace (the dialog reads the store), so
        // only offer it on the current one.
        ...(isCurrent
          ? [{
              id: 'settings',
              label: 'Workspace settings',
              icon: icon(Settings),
              onSelect: () => openWorkspaceSettingsDialog(),
            } as ContextMenuItem]
          : []),
        { id: 'rename', label: 'Rename', icon: icon(Pencil), onSelect: () => beginRename(ws) },
        {
          id: 'duplicate',
          label: 'Duplicate',
          icon: icon(Copy),
          separatorAfter: true,
          onSelect: () => void duplicateWorkspace(ws),
        },
        {
          id: 'delete',
          label: 'Delete workspace…',
          icon: icon(Trash2),
          tone: 'danger',
          onSelect: () => setConfirmDeleteWs(ws),
        },
      ];
    }

    const sameWs = target.wsId === workspace.id;
    return [
      {
        id: 'open',
        label: sameWs ? t('workspace.panels.showOnCanvas') : t('workspace.panels.openInItsWorkspace'),
        icon: icon(sameWs ? CornerDownRight : ExternalLink),
        separatorAfter: true,
        onSelect: () => openApp(target.wsId, target.app),
      },
      {
        id: 'remove',
        label: 'Remove from workspace',
        icon: icon(Trash2),
        tone: 'danger',
        onSelect: () => void removeApp(target.wsId, target.app),
      },
    ];
  }, [ctx.target, workspace.id, openWorkspace, browseFiles, beginRename, duplicateWorkspace, openApp, removeApp]);

  // --- filtered roots ------------------------------------------------------
  const visibleRoots = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? roots.filter((w) => (w.name || '').toLowerCase().includes(q)) : roots;
    // Current workspace pinned first, then the rest in their server order.
    return list.slice().sort((a, b) => {
      if (a.id === workspace.id) return -1;
      if (b.id === workspace.id) return 1;
      return 0;
    });
  }, [roots, search, workspace.id]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-1.5 px-2 py-2">
        <PanelSearchInput
          className="flex-1"
          value={search}
          onChange={setSearch}
          placeholder={t('workspace.panels.filterWorkspaces')}
        />
        {/* The Spaces panel header owns "+", so we don't double it here. */}
        {!scopeToCurrent && !hideCreate && (
          <button
            type="button"
            onClick={() => void newWorkspace()}
            title={t('workspace.canvas.newWorkspace')}
            aria-label={t('workspace.canvas.newWorkspace')}
            className="inline-flex h-8 shrink-0 items-center gap-1 rounded-lg bg-foreground/8 px-2 text-xs font-medium hover:bg-foreground/12"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>New</span>
          </button>
        )}
      </div>

      {/* List */}
      <div className="min-h-0 flex-1 overflow-auto px-2.5 pb-3">
        {loadingRoots ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : visibleRoots.length === 0 ? (
          <div className="px-3 py-8 text-center text-xs text-muted-foreground">
            {search.trim() ? t('workspace.panels.noWorkspacesMatch') : t('workspace.panels.noWorkspacesYet')}
            {!search.trim() && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => void newWorkspace()}
                  className="inline-flex items-center gap-1 rounded-lg bg-foreground/8 px-2 py-1 font-medium text-foreground hover:bg-foreground/12"
                >
                  <Plus className="h-3.5 w-3.5" /> {t('workspace.panels.createOne')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <ul className="space-y-0.5">
            {visibleRoots.map((ws) => {
              const isCurrent = ws.id === workspace.id;
              const open = expanded.has(ws.id);
              const renaming = renamingId === ws.id;
              const wsBind = ctx.bindTrigger({ kind: 'workspace', ws });
              return (
                <li key={ws.id}>
                  <div
                    {...wsBind}
                    // A row IS the open-workspace control, so say so to the
                    // accessibility tree (the live tree exposed only the
                    // filter box): button role + keyboard activation, named
                    // by its visible content. While the inline rename input
                    // is up the row must NOT be a button (no interactive
                    // descendants inside a button role).
                    role={renaming ? undefined : 'button'}
                    tabIndex={renaming ? undefined : 0}
                    aria-current={isCurrent ? 'true' : undefined}
                    onKeyDown={(e) => {
                      if (renaming || (e.key !== 'Enter' && e.key !== ' ')) return;
                      if (e.target !== e.currentTarget) return;
                      e.preventDefault();
                      openWorkspace(ws);
                    }}
                    onClick={() => {
                      if (renaming) return;
                      openWorkspace(ws);
                    }}
                    className={cn(
                      'group/row flex cursor-default items-center gap-1.5 rounded-lg py-1 pl-1.5 pr-1.5 text-left transition-colors',
                      'hover:bg-foreground/6',
                      isCurrent && 'bg-foreground/[0.04]',
                    )}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpanded((s) => {
                          const n = new Set(s);
                          if (n.has(ws.id)) n.delete(ws.id);
                          else n.add(ws.id);
                          return n;
                        });
                      }}
                      aria-label={open ? t('tools.collapse') : t('workspace.panels.expand')}
                      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-muted-foreground/70 hover:text-foreground"
                    >
                      <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} />
                    </button>
                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-indigo-500/10 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-400">
                      <LayoutGrid className="h-4 w-4" />
                    </span>
                    {renaming ? (
                      <input
                        autoFocus
                        value={renameDraft}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={() => void commitRename(ws)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void commitRename(ws);
                          else if (e.key === 'Escape') setRenamingId(null);
                        }}
                        maxLength={120}
                        className="min-w-0 flex-1 rounded-md bg-background px-1.5 py-0.5 text-xs outline-none ring-1 ring-ring/40"
                      />
                    ) : (
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-medium">{ws.name || 'Untitled'}</span>
                        <span className="block truncate text-[10px] text-muted-foreground">{wsSummaryLine(ws)}</span>
                      </span>
                    )}
                    {!renaming && isCurrent && (
                      <span
                        className="ml-1 shrink-0 text-[10px] font-semibold text-primary group-hover/row:opacity-0"
                        title={t('workspace.panels.currentWorkspace')}
                      >
                        ● current
                      </span>
                    )}
                    {!renaming && (
                      <button
                        type="button"
                        aria-label={t('workspace.panels.actions')}
                        onClick={(e) => {
                          e.stopPropagation();
                          wsBind.onContextMenu(e);
                        }}
                        className={cn(
                          'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-colors',
                          'hover:bg-foreground/10 hover:text-foreground focus:opacity-100 group-hover/row:opacity-100',
                          '-ml-1',
                        )}
                      >
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Children: open apps + the files hand-off */}
                  {open && (
                    <ul className="space-y-0.5">
                      {ws.apps.map((app) => {
                        const appBind = ctx.bindTrigger({ kind: 'app', wsId: ws.id, app });
                        return (
                          <li key={app.itemId}>
                            <div
                              {...appBind}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key !== 'Enter' && e.key !== ' ') return;
                                if (e.target !== e.currentTarget) return;
                                e.preventDefault();
                                openApp(ws.id, app);
                              }}
                              onClick={() => openApp(ws.id, app)}
                              style={{ paddingLeft: 34 }}
                              className={cn(
                                'group/app flex cursor-default items-center gap-1.5 rounded-lg py-1 pr-1.5 text-left transition-colors hover:bg-foreground/6',
                                app.minimized && 'opacity-70',
                              )}
                            >
                              <AppIcon app={app} />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-xs">{app.name}</span>
                                <span className="block truncate text-[10px] text-muted-foreground">
                                  {appKindLine(app)}
                                </span>
                              </span>
                              <button
                                type="button"
                                aria-label={t('workspace.panels.actions')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  appBind.onContextMenu(e);
                                }}
                                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-colors hover:bg-foreground/10 hover:text-foreground focus:opacity-100 group-hover/app:opacity-100"
                              >
                                <MoreHorizontal className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </li>
                        );
                      })}
                      {ws.appCount > ws.apps.length && (
                        <li
                          style={{ paddingLeft: 34 }}
                          className="py-0.5 text-[10px] text-muted-foreground/70"
                        >
                          and {ws.appCount - ws.apps.length} more on the canvas
                        </li>
                      )}
                      {ws.apps.length === 0 && (
                        <li
                          style={{ paddingLeft: 34 }}
                          className="py-1 text-[11px] text-muted-foreground/70"
                        >
                          {t('workspace.panels.nothingOpenYet')}
                        </li>
                      )}
                      <li>
                        <button
                          type="button"
                          onClick={() => browseFiles(ws)}
                          style={{ paddingLeft: 34 }}
                          className="flex w-full cursor-default items-center gap-1.5 rounded-lg py-1 pr-1.5 text-left transition-colors hover:bg-foreground/6"
                        >
                          <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-foreground/8 text-muted-foreground">
                            <FolderOpen className="h-3.5 w-3.5" />
                          </span>
                          <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-primary">
                            {t('workspace.panels.browseFiles')}
                          </span>
                          <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground/70" />
                        </button>
                      </li>
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <FileContextMenu items={menuItems} anchor={ctx.anchor} onClose={ctx.close} ariaLabel="Workspace actions" />

      {confirmDeleteWs && (
        <DeleteWorkspaceDialog
          ws={confirmDeleteWs}
          onCancel={() => setConfirmDeleteWs(null)}
          onConfirm={() => void deleteWorkspace(confirmDeleteWs)}
        />
      )}

      {shareWorkspaceId && (
        <ShareDialog kind="workspace" workspaceId={shareWorkspaceId} onClose={() => setShareWorkspaceId(null)} />
      )}

      {namePrompt && (
        <NameDialog
          title={namePrompt.title}
          placeholder={namePrompt.placeholder}
          submitLabel={namePrompt.submitLabel}
          onSubmit={(name) => {
            namePrompt.onSubmit(name);
            setNamePrompt(null);
          }}
          onCancel={() => setNamePrompt(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workspace delete confirm
// ---------------------------------------------------------------------------

function DeleteWorkspaceDialog({
  ws,
  onCancel,
  onConfirm,
}: {
  ws: WsRoot;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const t = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const [confirmText, setConfirmText] = useState('');
  const armed = confirmText.trim() === ws.name.trim() && ws.name.trim().length > 0;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    inputRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <PopoverPortal
      role="dialog"
      ariaLabel={t('workspace.canvas.delete')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 240,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6vh 6vw',
      }}
      className="bg-black/40 backdrop-blur-sm"
    >
      <div
        className="glass-panel w-[min(420px,94vw)] rounded-2xl p-4 shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className="text-sm font-semibold">{t('workspace.canvas.delete')}</h2>
        <p className="mt-1.5 text-xs text-muted-foreground">
          {t('workspace.panels.deleteWarnPre')} <span className="font-medium text-foreground">{ws.name || t('widget.untitled')}</span>{t('workspace.panels.deleteWarnPost')}
        </p>
        <p className="mt-3 text-[11px] text-muted-foreground">
          {t('workspace.panels.typeNameToConfirm')}
        </p>
        <input
          ref={inputRef}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && armed) onConfirm();
          }}
          placeholder={ws.name}
          className="mt-1 h-9 w-full rounded-lg bg-foreground/5 px-2.5 text-sm outline-none ring-1 ring-transparent focus:ring-ring/40"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:bg-foreground/6 hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!armed}
            onClick={onConfirm}
            className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:bg-red-700 disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </div>
    </PopoverPortal>
  );
}

// ---------------------------------------------------------------------------
// Inline name dialog (new workspace)
// ---------------------------------------------------------------------------

function NameDialog({
  title,
  placeholder,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  title: string;
  placeholder: string;
  submitLabel: string;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    inputRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);
  const armed = value.trim().length > 0;

  return (
    <PopoverPortal
      role="dialog"
      ariaLabel={title}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 240,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6vh 6vw',
      }}
      className="bg-black/40 backdrop-blur-sm"
    >
      <div
        className="glass-panel w-[min(400px,94vw)] rounded-2xl p-4 shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className="text-sm font-semibold">{title}</h2>
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && armed) onSubmit(value.trim());
          }}
          placeholder={placeholder}
          maxLength={120}
          className="mt-3 h-9 w-full rounded-lg bg-foreground/5 px-2.5 text-sm outline-none ring-1 ring-transparent focus:ring-ring/40"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:bg-foreground/6 hover:text-foreground"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!armed}
            onClick={() => onSubmit(value.trim())}
            className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </PopoverPortal>
  );
}
